import type { VirtualItem } from '@tanstack/react-virtual';
import { useVirtualizer, useWindowVirtualizer } from '@tanstack/react-virtual';
import type { RefObject } from 'react';
import { useRef, useState } from 'react';
import { useIsomorphicLayoutEffect } from 'usehooks-ts';

import { shouldAnimateScroll } from '@utils/dom-utils';

/**
 * Default per-row height guess (px) used before a row has been measured.
 */
export const DEFAULT_ESTIMATE_SIZE = 44;

/**
 * Default number of rows rendered beyond the viewport on each side.
 */
export const DEFAULT_OVERSCAN = 8;

/**
 * Windowing knobs shared by the hook and its component wrappers.
 */
export interface VirtualWindowOptions {
  /**
   * Initial per-row height guess (px) before measurement.
   */
  estimateSize?: number;
  /**
   * Returns the scrollable container element; omit for window/document scroll.
   */
  getScrollElement?: () => HTMLElement | null;
  /**
   * Rows rendered beyond the viewport on each side.
   */
  overscan?: number;
}

export interface UseVirtualRowsOptions extends VirtualWindowOptions {
  /**
   * Number of items to window.
   */
  count: number;
  /**
   * Whether windowing is active. When false the virtualizer stays inert, so the
   * hook can be mounted unconditionally and switched on later.
   */
  enabled?: boolean;
  /**
   * Element whose top edge marks where the list begins; its document offset
   * becomes the window virtualizer's `scrollMargin` (ignored in container mode).
   */
  scrollMarginRef: RefObject<HTMLElement>;
  /**
   * Padding (px) kept clear at the viewport start when scrolling a row into view.
   */
  scrollPaddingStart?: number;
}

export interface ScrollToIndexOptions {
  /**
   * How to align the row within the viewport.
   */
  align?: 'auto' | 'center' | 'end' | 'start';
  /**
   * Requested scroll animation; `smooth` is downgraded to instant over a trip
   * longer than the scrollport, or under a reduced-motion preference.
   */
  behavior?: 'instant' | 'smooth';
}

export interface UseVirtualRowsResult {
  /**
   * Force a re-measure of all rows (e.g. after the row set is replaced).
   */
  measure: () => void;
  /**
   * Ref callback to attach to each rendered row for dynamic height measurement.
   */
  measureElement: (node: Element | null) => void;
  /**
   * Offset (px) to subtract from `virtualItem.start` when positioning rows.
   */
  scrollMargin: number;
  /**
   * Scroll the row at the given index into view, whether or not it is currently
   * rendered.
   */
  scrollToIndex: (index: number, options?: ScrollToIndexOptions) => void;
  /**
   * Scroll the list to an absolute offset (px).
   */
  scrollToOffset: (offset: number) => void;
  /**
   * Total height (px) of all rows — the size of the spacer.
   */
  totalSize: number;
  /**
   * The rows currently within the viewport plus overscan.
   */
  virtualItems: VirtualItem[];
}

/**
 * Headless row windowing over `@tanstack/react-virtual` — renders only the rows
 * in the viewport (plus overscan); the caller renders each row. Window scroll is
 * the default; pass `getScrollElement` for a container-scrolled list. The scroll
 * mode is fixed for the consumer's lifetime.
 */
export const useVirtualRows = ({
  count,
  enabled = true,
  estimateSize = DEFAULT_ESTIMATE_SIZE,
  getScrollElement,
  overscan = DEFAULT_OVERSCAN,
  scrollMarginRef,
  scrollPaddingStart,
}: UseVirtualRowsOptions): UseVirtualRowsResult => {
  const isContainer = Boolean(getScrollElement);

  // The mode picks the single virtualizer below, so toggling it mid-life would
  // corrupt React's hook order — fail loudly instead.
  const initialIsContainer = useRef(isContainer);
  if (initialIsContainer.current !== isContainer) {
    throw new Error(
      'useVirtualRows: the scroll mode (whether getScrollElement is provided) must not change across renders.',
    );
  }

  // Window mode needs the list's document offset to know when rows enter view.
  const [scrollMargin, setScrollMargin] = useState(0);

  // The target is absent until rows first mount; key on row presence (not count)
  // so page appends don't trigger a reflow read each fetch.
  const hasRows = count > 0;
  useIsomorphicLayoutEffect(() => {
    if (!enabled || isContainer) return undefined;

    const measureScrollMargin = () => {
      const element = scrollMarginRef.current;
      if (!element) return;

      const next = element.getBoundingClientRect().top + window.scrollY;
      setScrollMargin(prev => (prev === next ? prev : next));
    };

    measureScrollMargin();
    window.addEventListener('resize', measureScrollMargin);
    return () => window.removeEventListener('resize', measureScrollMargin);
  }, [enabled, hasRows, isContainer, scrollMarginRef]);

  // Fork on the stable mode so each consumer mounts one virtualizer, not two; the
  // conditional call is safe since the mode never changes (asserted above).
  /* eslint-disable react-hooks/rules-of-hooks */
  const virtualizer = isContainer
    ? useVirtualizer({
        count,
        enabled,
        estimateSize: () => estimateSize,
        getScrollElement: getScrollElement ?? (() => null),
        overscan,
        scrollPaddingStart,
      })
    : useWindowVirtualizer({
        count,
        enabled,
        estimateSize: () => estimateSize,
        overscan,
        scrollMargin,
        scrollPaddingStart,
      });
  /* eslint-enable react-hooks/rules-of-hooks */

  // Over a long trip react-virtual re-issues the scroll as measurement moves the
  // target, restarting the animation for many frames before it snaps.
  const scrollToIndex: UseVirtualRowsResult['scrollToIndex'] = (
    index,
    options,
  ) => {
    const [targetOffset] =
      virtualizer.getOffsetForIndex(index, options?.align) ?? [];
    const distance =
      targetOffset === undefined
        ? Infinity
        : Math.abs(targetOffset - (virtualizer.scrollOffset ?? 0));

    // Container lists are sized by their scrollport, not the window. An absent
    // element means no measurable trip, so it jumps rather than falling back to
    // the window height and animating against the wrong viewport.
    const viewport = isContainer
      ? (getScrollElement?.()?.clientHeight ?? 0)
      : undefined;

    virtualizer.scrollToIndex(index, {
      ...options,
      behavior:
        options?.behavior === 'smooth' &&
        shouldAnimateScroll(distance, viewport)
          ? 'smooth'
          : 'instant',
    });
  };

  return {
    measure: virtualizer.measure,
    measureElement: virtualizer.measureElement,
    scrollMargin: isContainer ? 0 : scrollMargin,
    scrollToIndex,
    scrollToOffset: virtualizer.scrollToOffset,
    totalSize: virtualizer.getTotalSize(),
    virtualItems: virtualizer.getVirtualItems(),
  };
};
